/**
 * VideoProvider - Native video element wrapper
 * Proxies events to PlayerState and handles playback control
 */

/**
 * VideoProvider class - wraps native video element
 */
function VideoProvider(video, playerState) {
    this._video = video;
    this._playerState = playerState;
    this._eventHandlers = {};
    this._destroyed = false;
}

/**
 * Get video element
 */
VideoProvider.prototype.getVideo = function () {
    return this._video;
};

/**
 * Setup video element event listeners
 */
VideoProvider.prototype.setup = function () {
    var self = this;
    var video = self._video;
    var state = self._playerState;

    console.log('[VideoProvider] Setting up event listeners');

    // Helper to add event listener and track for cleanup
    function addEvent(name, handler) {
        video.addEventListener(name, handler);
        self._eventHandlers[name] = handler;
    }

    // Playback state events
    addEvent('play', function () {
        state.batch({
            playing: true,
            paused: false,
            ended: false,
            waiting: false,
        });
    });

    addEvent('pause', function () {
        state.batch({
            playing: false,
            paused: true,
        });
    });

    addEvent('playing', function () {
        state.batch({
            playing: true,
            paused: false,
            waiting: false,
        });
    });

    addEvent('waiting', function () {
        state.set('waiting', true);
    });

    addEvent('ended', function () {
        state.batch({
            ended: true,
            playing: false,
            paused: true,
        });
    });

    // Time events
    addEvent('timeupdate', function () {
        state.set('currentTime', video.currentTime);
    });

    addEvent('durationchange', function () {
        state.set('duration', video.duration);
    });

    addEvent('progress', function () {
        if (video.buffered.length > 0) {
            var bufferedEnd = video.buffered.end(video.buffered.length - 1);
            state.batch({
                buffered: bufferedEnd,
                bufferedEnd: bufferedEnd,
            });
        }
    });

    // Seeking events
    addEvent('seeking', function () {
        state.set('seeking', true);
    });

    addEvent('seeked', function () {
        state.set('seeking', false);
    });

    // Volume events
    addEvent('volumechange', function () {
        state.batch({
            volume: video.volume,
            muted: video.muted,
        });
    });

    // Ready state events
    addEvent('loadedmetadata', function () {
        state.batch({
            duration: video.duration,
            isReady: true,
        });
        // Note: text track extraction removed from here to prevent race condition
        // with addTextTracks(). External subtitles are managed solely by addTextTracks().
    });

    addEvent('canplay', function () {
        state.batch({
            canPlay: true,
            loading: false,
        });
    });

    addEvent('loadstart', function () {
        state.set('loading', true);
    });

    // Error event
    addEvent('error', function (e) {
        var error = video.error;
        state.set('error', {
            code: error ? error.code : -1,
            message: error ? error.message : 'Unknown error',
        });
    });

    // Fullscreen change (with legacy prefixes)
    var handleFullscreenChange = function () {
        var isFullscreen = !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
        );
        state.set('fullscreen', isFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    self._fullscreenHandler = handleFullscreenChange;
};

/**
 * Play video
 */
VideoProvider.prototype.play = function () {
    var self = this;
    if (!self._video) return Promise.reject(new Error('No video element'));

    var playPromise = self._video.play();
    if (playPromise !== undefined) {
        return playPromise.catch(function (err) {
            console.warn('[VideoProvider] Play failed:', err.message);

            // Try muted autoplay as fallback
            if (err.name === 'NotAllowedError') {
                self._video.muted = true;
                self._playerState.set('muted', true);
                return self._video.play();
            }
            throw err;
        });
    }
    return Promise.resolve();
};

/**
 * Pause video
 */
VideoProvider.prototype.pause = function () {
    if (this._video) {
        this._video.pause();
    }
};

/**
 * Seek to time
 */
VideoProvider.prototype.seek = function (time) {
    if (this._video) {
        var duration = this._video.duration || 0;
        this._video.currentTime = Math.max(0, Math.min(duration, time));
    }
};

/**
 * Set volume
 */
VideoProvider.prototype.setVolume = function (volume) {
    if (this._video) {
        this._video.volume = Math.max(0, Math.min(1, volume));
    }
};

/**
 * Set muted
 */
VideoProvider.prototype.setMuted = function (muted) {
    if (this._video) {
        this._video.muted = muted;
    }
};

/**
 * Set source directly (for MP4)
 */
VideoProvider.prototype.setSource = function (src, type) {
    if (this._video) {
        this._video.src = src;
        this._playerState.set('source', src);
        this._playerState.set('sourceType', type || 'video/mp4');
    }
};

/**
 * Load video
 */
VideoProvider.prototype.load = function () {
    if (this._video) {
        this._video.load();
    }
};

/**
 * Enter fullscreen
 */
VideoProvider.prototype.enterFullscreen = function (container) {
    var element = container || this._video;

    if (element.requestFullscreen) {
        return element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
    } else if (element.webkitRequestFullScreen) {
        element.webkitRequestFullScreen();
    } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
    } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
    } else if (this._video.webkitEnterFullscreen) {
        // iOS video-specific
        this._video.webkitEnterFullscreen();
    }

    return Promise.resolve();
};

/**
 * Exit fullscreen
 */
VideoProvider.prototype.exitFullscreen = function () {
    if (document.exitFullscreen) {
        return document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
    } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
    } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
    }

    return Promise.resolve();
};

/**
 * Add external text tracks (VTT subtitles)
 * Creates HTML5 track elements and populates textTracks state
 * @param {Array} subtitles - Array of subtitle objects with { src, label, srcLang }
 */
VideoProvider.prototype.addTextTracks = function (subtitles) {
    if (!subtitles || !Array.isArray(subtitles)) return;

    var self = this;
    var video = self._video;
    var tracks = [];

    console.log('[VideoProvider] addTextTracks called with', subtitles.length, 'subtitles');

    // Clear existing track elements to prevent duplicates
    var existingTracks = video.querySelectorAll('track');
    console.log('[VideoProvider] Removing', existingTracks.length, 'existing track elements');
    for (var j = 0; j < existingTracks.length; j++) {
        video.removeChild(existingTracks[j]);
    }

    for (var i = 0; i < subtitles.length; i++) {
        var sub = subtitles[i];
        if (!sub || !sub.src) continue;

        // Create track element using vanilla JS for legacy compatibility
        var track = document.createElement('track');
        track.kind = 'subtitles';
        track.src = sub.src;
        track.label = sub.label || sub.srcLang || sub.srclang || ('Track ' + (i + 1));
        track.srclang = sub.srcLang || sub.srclang || sub.lang || 'und';

        // Check if track should be default
        var isDefault = sub.default === true;
        track.default = isDefault;

        // Set mode
        track.mode = isDefault ? 'showing' : 'hidden';

        video.appendChild(track);

        tracks.push({
            index: i,
            label: track.label,
            language: track.srclang,
            src: sub.src
        });
        
        if (isDefault) {
            self._playerState.set('currentTextTrack', i);
        }

        console.log('[VideoProvider] Added track:', track.label, track.srclang, isDefault ? '(DEFAULT)' : '');
    }

    // Update state with available tracks
    if (tracks.length > 0) {
        self._playerState.set('textTracks', tracks);
        console.log('[VideoProvider] textTracks state updated with', tracks.length, 'tracks');
    }
};

/**
 * Cleanup
 */
VideoProvider.prototype.destroy = function () {
    if (this._destroyed) return;
    this._destroyed = true;

    var self = this;
    var video = self._video;

    // Remove video event listeners
    Object.keys(self._eventHandlers).forEach(function (name) {
        video.removeEventListener(name, self._eventHandlers[name]);
    });

    // Remove fullscreen listeners
    if (self._fullscreenHandler) {
        document.removeEventListener('fullscreenchange', self._fullscreenHandler);
        document.removeEventListener('webkitfullscreenchange', self._fullscreenHandler);
        document.removeEventListener('mozfullscreenchange', self._fullscreenHandler);
        document.removeEventListener('MSFullscreenChange', self._fullscreenHandler);
    }

    // Clear video source
    video.removeAttribute('src');
    video.load();

    self._eventHandlers = {};
    console.log('[VideoProvider] Destroyed');
};

export { VideoProvider };
